import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';
import ModalContentWrapper from 'components/ModalContentWrapper';

import AssignMemberSuccess from './AssignMemberSuccess';
import AssignMembersToDevice from './AssignMembersToDevices';
import ManageMembers from './ManageMembers';

type AssignMembersToDevicesStage = 'manage' | 'members' | 'success';

interface AssignMembersToDevicesModalState {
  stage: AssignMembersToDevicesStage;
  members: number[];
}

export interface AssignMembersToDevicesModalStageProps {
  open: boolean;
  state: AssignMembersToDevicesModalState;
  setState: Dispatch<SetStateAction<AssignMembersToDevicesModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  organizationId: number | null;
  deviceId: number | null;
  hasAssignedMembers: boolean;
  selectedDevices: number[];
}

const StageComponents: Record<
  AssignMembersToDevicesStage,
  FC<AssignMembersToDevicesModalStageProps>
> = {
  manage: ManageMembers,
  members: AssignMembersToDevice,
  success: AssignMemberSuccess,
};

interface Props extends DefaultPopupProps {
  organizationId: number | null;
  deviceId: number | null;
  hasAssignedMembers: boolean;
  selectedDevices: number[];
}

const AssignMembersToDevicesModal: FC<Props> = ({
  onClose,
  open,
  organizationId,
  deviceId,
  hasAssignedMembers,
  selectedDevices,
}) => {
  const [state, setState] = useState<AssignMembersToDevicesModalState>({
    stage: 'members',
    members: [],
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, members: [] }));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setState(prev => ({
        ...prev,
        stage: hasAssignedMembers ? 'manage' : 'members',
      }));
    }
  }, [open, hasAssignedMembers]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    switch (state.stage) {
      case 'manage':
        setState(prev => ({ ...prev, members: [] }));
        onClose();
        break;

      case 'members':
        if (!hasAssignedMembers) {
          setState(prev => ({ ...prev, members: [] }));
          onClose();
        } else {
          setState(prev => ({ ...prev, members: [], stage: 'manage' }));
        }
        break;

      case 'success': {
        setState(prev => ({ ...prev }));
      }
    }
  }, [hasAssignedMembers, onClose, state.stage]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'manage' && hasAssignedMembers ? (
          <ArrowBackRoundedIcon />
        ) : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <ModalContentWrapper data-testid="assign-members-to-devices-modal">
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          organizationId={organizationId}
          deviceId={deviceId}
          hasAssignedMembers={hasAssignedMembers}
          selectedDevices={selectedDevices}
        />
      </ModalContentWrapper>
    </Modal>
  );
};

export default AssignMembersToDevicesModal;
